<script lang="ts" module>
  import { reactiveContext } from "#lib/internal/helpers"

  const { getContext, setContext } = reactiveContext({
    variant: null as VariantProps<typeof alertVariants>["variant"],
  })

  export const getAlertRootContext = getContext
</script>

<script lang="ts">
  import type { BaseProps } from "#lib/internal/types"
  import { cn } from "#lib/utils"
  import { Close } from "@tastyworks/icons-2.0"
  import type { VariantProps } from "class-variance-authority"
  import type { Snippet } from "svelte"
  import { Alert, alertVariants } from "."

  type Props = BaseProps<"div"> & {
    dismissible?: boolean
    variant: VariantProps<typeof alertVariants>["variant"]
    size?: VariantProps<typeof alertVariants>["size"]
    show?: boolean
    icon?: Snippet
    children?: Snippet
    ondismiss?: () => void
  }

  let {
    class: className = undefined,
    dismissible = false,
    variant,
    size = "full",
    show = $bindable(true),
    icon,
    children,
    ondismiss,
    ...restProps
  }: Props = $props()

  const rootCtx = setContext({
    variant: (v) => (variant = v),
  })
  $effect(() => {
    rootCtx.set({ variant })
  })

  function handleDismiss() {
    show = false
    ondismiss?.()
  }
</script>

<div
  class={cn(alertVariants({ variant, size }), className)}
  {...restProps}
  class:hidden={!show}
  role="alert"
>
  {#if dismissible}
    <Close
      class="relative float-right ml-4 h-6 w-6 cursor-pointer text-general-secondary-label font-x-large-600"
      onclick={handleDismiss}
    />
  {/if}

  <div class="flex gap-3">
    {#if icon}
      {@render icon()}
    {:else}
      <Alert.Icon />
    {/if}

    <div class="flex flex-col gap-0.5">
      {@render children?.()}
    </div>
  </div>
</div>
